import { useTranslation } from 'react-i18next';
import SarSymbol from '@/components/SarSymbol';
import { Separator } from '@/components/ui/separator';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { GrossPrice, VatIncludedLabel } from '@/components/billing/PricingBreakdown';
import type { PricingDisplayContract } from '@/lib/billing/pricingDisplay';
import { formatSarAmount } from '@/lib/canonicalMoney';

export interface AuthoritativeUpgradePricing {
  differenceBeforeVatSar: number;
  vatRatePercent: number;
  vatAmountSar: number;
  totalDueTodaySar: number;
  totalDueTodayHalalas: number;
  nextRenewalPriceSar: number;
  nextRenewalDate: string;
  pricingDisplay: PricingDisplayContract;
  targetPlanPricingDisplay: PricingDisplayContract;
  paidCycleCreditPricingDisplay: PricingDisplayContract;
  headlineRoundingAdjustmentSar: number;
  renewalPricingDisplay: PricingDisplayContract;
}

const asMoney = (value: number) => formatSarAmount(value, 'upgrade_summary');

export function UpgradePricingSummary({
  pricing,
  className = '',
}: {
  pricing: AuthoritativeUpgradePricing;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;

  return (
    <div
      className={`space-y-3 rounded-lg border border-border p-4 ${className}`}
      data-testid="upgrade-pricing-summary"
    >
      <GrossHeadlineLine
        label={t('commercialPlanChange.fields.targetGrossValue')}
        pricing={pricing.targetPlanPricingDisplay}
      />
      <GrossHeadlineLine
        label={t('commercialPlanChange.fields.paidGrossCredit')}
        pricing={pricing.paidCycleCreditPricingDisplay}
      />
      {Math.abs(pricing.headlineRoundingAdjustmentSar) >= 0.005 ? (
        <p
          className="text-xs leading-relaxed text-muted-foreground"
          data-testid="upgrade-headline-rounding-note"
        >
          {t('commercialPlanChange.fields.roundingExplanation', {
            amount: asMoney(Math.abs(pricing.headlineRoundingAdjustmentSar)),
          })}
        </p>
      ) : null}
      <Separator />
      <PricingLine
        label={t('commercialPlanChange.fields.differenceBeforeVat')}
        value={asMoney(pricing.pricingDisplay.net_amount_sar)}
      />
      <PricingLine
        label={t('commercialPlanChange.fields.vat', { rate: pricing.pricingDisplay.vat_rate_percent })}
        value={asMoney(pricing.pricingDisplay.vat_amount_sar)}
      />
      <PricingLine
        label={t('commercialPlanChange.fields.dueToday')}
        value={asMoney(pricing.pricingDisplay.gross_amount_sar)}
        emphasized
      />
      <VatIncludedLabel className="block text-end" />
      <Separator />
      <div className="flex items-start justify-between gap-4 text-sm">
        <span className="text-muted-foreground">{t('commercialPlanChange.fields.nextRenewalGross')}</span>
        <GrossPrice pricing={pricing.renewalPricingDisplay} size="small" />
      </div>
      <PricingLine
        label={t('commercialPlanChange.fields.renewalDate')}
        value={formatLocalizedDate(
          pricing.nextRenewalDate,
          'MMM dd, yyyy',
          language,
        )}
        monetary={false}
      />
    </div>
  );
}

function GrossHeadlineLine({
  label,
  pricing,
}: {
  label: string;
  pricing: PricingDisplayContract;
}) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="min-w-0 text-muted-foreground">{label}</span>
      <div className="shrink-0 text-end">
        <span className="flex items-center justify-end gap-1 font-mono font-semibold text-foreground">
          <SarSymbol size={11} />
          {asMoney(pricing.gross_amount_sar)}
        </span>
        <VatIncludedLabel className="mt-0.5 block" />
      </div>
    </div>
  );
}

function PricingLine({
  label,
  value,
  emphasized = false,
  monetary = true,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
  monetary?: boolean;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-4 ${
        emphasized ? 'font-semibold text-foreground' : 'text-sm'
      }`}
    >
      <span className="min-w-0 text-muted-foreground">{label}</span>
      <span className="flex shrink-0 items-center gap-1 font-mono">
        {monetary ? <SarSymbol size={11} /> : null}
        {value}
      </span>
    </div>
  );
}
